import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
    setDropdownOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const textClass = isScrolled
    ? "text-foreground-900"
    : "text-background-50";

  const logoBgClass = isScrolled
    ? "bg-primary-500"
    : "bg-background-50/20 backdrop-blur-sm";

  const logoIconClass = isScrolled
    ? "text-background-50"
    : "text-background-50";

  const dropdownBtnClass = isScrolled
    ? "bg-background-100 hover:bg-background-200 text-foreground-800"
    : "bg-background-50/15 backdrop-blur-sm hover:bg-background-50/25 text-background-50";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">

          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`ri-menu-line text-xl ${textClass}`}></i>
            </button>

            <Link to="/" className="flex items-center gap-2 group">
              <div className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg transition-colors ${logoBgClass}`}>
                <i className={`ri-file-list-3-line text-lg ${logoIconClass}`}></i>
              </div>
              <span className={`font-heading text-lg md:text-xl font-semibold whitespace-nowrap transition-colors hidden sm:inline ${textClass}`}>
                Mulebuy Spreadsheet
              </span>
            </Link>

            <Link
              to="/"
              className={`hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${dropdownBtnClass}`}
            >
              <i className="ri-home-line text-sm"></i>
              Home
            </Link>

            <Link
              to="/blog/what-is-mulebuy-spreadsheet-complete-beginner-guide"
              className={`hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${dropdownBtnClass}`}
            >
              <i className="ri-book-open-line text-sm"></i>
              Shopping Guide
            </Link>
          </div>

          <div className="flex items-center gap-1.5 md:gap-4">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${dropdownBtnClass}`}
              >
                <i className="ri-grid-line text-sm md:text-base"></i>
                <span className="hidden sm:inline">Categories</span>
                <i
                  className={`ri-arrow-down-s-line text-xs md:text-sm transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-start gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-accent-50 hover:text-accent-800 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden mt-0.5">
                        <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <span className="font-medium block whitespace-nowrap">{cat.name}</span>
                        <span className="block text-xs text-foreground-500 mt-0.5">{cat.description}</span>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-md bg-secondary-100 text-secondary-800 hover:bg-secondary-200 text-sm font-medium transition-colors whitespace-nowrap"
            >
              Mulebuy Spreadsheet
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1 md:gap-1.5 px-3 md:px-5 py-2 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 text-xs md:text-sm font-semibold transition-colors whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-shopping-cart-line text-xs md:text-sm"></i>
            </a>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-900 hover:bg-background-100 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-line text-lg text-foreground-500"></i>
              <span className="font-medium text-sm">Home</span>
            </Link>

            <Link
              to="/blog/what-is-mulebuy-spreadsheet-complete-beginner-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-900 hover:bg-background-100 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-book-open-line text-lg text-foreground-500"></i>
              <span className="font-medium text-sm">Shopping Guide</span>
            </Link>

            <button
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <i className="ri-grid-line text-lg text-foreground-500"></i>
                <span className="font-medium text-sm">Categories</span>
              </div>
              <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 ${mobileDropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {mobileDropdownOpen && (
              <div className="ml-4 space-y-1 border-l-2 border-background-200 pl-4">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-foreground-700 hover:bg-accent-50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="w-7 h-7 flex-shrink-0 rounded-md overflow-hidden">
                      <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-sm">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}

            <div className="pt-4 space-y-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-secondary-50 text-secondary-800 hover:bg-secondary-100 transition-colors"
              >
                <i className="ri-file-list-3-line text-lg text-secondary-600"></i>
                <span className="font-medium text-sm">Mulebuy Spreadsheet</span>
                <i className="ri-arrow-right-up-line text-xs ml-auto"></i>
              </a>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-shopping-cart-line text-lg"></i>
                <span className="font-semibold text-sm">Buy It Now</span>
                <i className="ri-arrow-right-up-line text-xs ml-auto"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}